import apiClient from './config';
import { ApiResponse } from '../../types';

export interface AIMesaira {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  citations?: Array<{ id: number; title: string }>;
  metadata?: {
    mood?: string;
    sentiment?: 'positive' | 'negative' | 'neutral';
    urgency?: 'low' | 'medium' | 'high' | 'critical';
    category?: 'general' | 'mental-health' | 'work-stress' | 'career' | 'relationships' | 'wellness-suggestions';
  };
}

export interface AIConversation {
  id: string;
  userId: string;
  title: string;
  mesairas: AIMesaira[];
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  summary?: string;
  tags?: string[];
}

export interface AIInsight {
  id: string;
  type: 'recommendation' | 'warning' | 'achievement' | 'tip';
  title: string;
  content: string;
  priority: 'low' | 'medium' | 'high';
  category: string;
  actionable: boolean;
  actionUrl?: string;
  createdAt: string;
  expiresAt?: string;
}

export interface MentalHealthAssessment {
  id: string;
  userId: string;
  score: number;
  category: 'excellent' | 'good' | 'fair' | 'poor' | 'critical';
  factors: {
    stress: number;
    anxiety: number;
    depression: number;
    burnout: number;
    satisfaction: number;
  };
  recommendations: string[];
  createdAt: string;
}

export const aiAPI = {
  // Chat with Aira AI (uses backend Ollama integration)
  sendMessage: async (data: {
    message: string;
    context?: string;
    conversationHistory?: Array<{ role: string; content: string }>;
  }): Promise<ApiResponse<{
    response: string;
    model: string;
    context: string;
    timestamp: string;
  }>> => {
    const response = await apiClient.post('/ai/chat', data);
    return response.data;
  },

  sendMesaira: async (data: {
    mesaira: string;
    conversationId?: string;
    context?: {
      mood?: string;
      currentPage?: string;
      userActivity?: any;
    };
  }): Promise<ApiResponse<{
    response: string;
    conversationId: string;
    mesairaId: string;
    metadata?: any;
  }>> => {
    const response = await apiClient.post('/ai/chat', data);
    return response.data;
  },

  checkHealth: async (): Promise<ApiResponse<{
    status: string;
    availableModels: any[];
    currentModel: string;
    timestamp: string;
  }>> => {
    const response = await apiClient.get('/ai/health');
    return response.data;
  },

  // Get AI conversations
  getConversations: async (): Promise<ApiResponse<AIConversation[]>> => {
    const response = await apiClient.get('/ai/conversations');
    return response.data;
  },

  // Get specific conversation
  getConversation: async (id: string): Promise<ApiResponse<AIConversation>> => {
    const response = await apiClient.get(`/ai/conversations/${id}`);
    return response.data;
  },

  // Delete conversation
  deleteConversation: async (id: string): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete(`/ai/conversations/${id}`);
    return response.data;
  },

  // Get AI insights and recommendations
  getInsights: async (params?: {
    type?: 'recommendation' | 'warning' | 'achievement' | 'tip';
    category?: string;
    limit?: number;
  }): Promise<ApiResponse<AIInsight[]>> => {
    const response = await apiClient.get('/ai/insights', { params });
    return response.data;
  },

  // Mark insight as read
  markInsightAsRead: async (id: string): Promise<ApiResponse<void>> => {
    const response = await apiClient.post(`/ai/insights/${id}/read`);
    return response.data;
  },

  // Get mental health assessment
  getMentalHealthAssessment: async (): Promise<ApiResponse<MentalHealthAssessment>> => {
    const response = await apiClient.get('/ai/mental-health-assessment');
    return response.data;
  },

  // Submit mental health check-in
  submitMentalHealthCheckin: async (data: {
    mood: number; // 1-10 scale
    stress: number; // 1-10 scale
    energy: number; // 1-10 scale
    sleep: number; // 1-10 scale
    notes?: string;
  }): Promise<ApiResponse<void>> => {
    const response = await apiClient.post('/ai/mental-health-checkin', data);
    return response.data;
  },

  // Get personalized recommendations
  getRecommendations: async (params?: {
    type?: 'learning' | 'wellness' | 'productivity' | 'social';
    limit?: number;
  }): Promise<ApiResponse<{
    learning?: any[];
    wellness?: any[];
    productivity?: any[];
    social?: any[];
  }>> => {
    const response = await apiClient.get('/ai/recommendations', { params });
    return response.data;
  },

  // Analyze user sentiment
  analyzeSentiment: async (text: string): Promise<ApiResponse<{
    sentiment: 'positive' | 'negative' | 'neutral';
    confidence: number;
    emotions: {
      joy: number;
      sadness: number;
      anger: number;
      fear: number;
      surprise: number;
    };
  }>> => {
    const response = await apiClient.post('/ai/sentiment-analysis', { text });
    return response.data;
  },

  // Crisis detection and intervention
  checkCrisisIndicators: async (data: {
    mesaira?: string;
    behaviorData?: any;
    context?: any;
  }): Promise<ApiResponse<{
    riskLevel: 'low' | 'medium' | 'high' | 'critical';
    indicators: string[];
    recommendations: string[];
    emergencyContacts?: any[];
    immediateActions?: string[];
  }>> => {
    const response = await apiClient.post('/ai/crisis-check', data);
    return response.data;
  },

  // Get AI-powered analytics
  getAnalytics: async (params?: {
    period?: 'day' | 'week' | 'month' | 'quarter' | 'year';
    metrics?: string[];
  }): Promise<ApiResponse<{
    engagement: any;
    wellbeing: any;
    productivity: any;
    learning: any;
    predictions: any;
  }>> => {
    const response = await apiClient.get('/ai/analytics', { params });
    return response.data;
  },

  // Voice synthesis (text-to-speech)
  synthesizeVoice: async (data: {
    text: string;
    voice?: 'aira' | 'professional' | 'friendly';
    speed?: number;
    pitch?: number;
  }): Promise<ApiResponse<{
    audioUrl: string;
    duration: number;
  }>> => {
    const response = await apiClient.post('/ai/voice-synthesis', data);
    return response.data;
  },

  // Speech recognition (speech-to-text)
  recognizeSpeech: async (audioBlob: Blob): Promise<ApiResponse<{
    text: string;
    confidence: number;
    language: string;
  }>> => {
    const formData = new FormData();
    formData.append('audio', audioBlob);

    const response = await apiClient.post('/ai/speech-recognition', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Smart notifications
  getSmartNotifications: async (): Promise<ApiResponse<{
    priority: any[];
    contextual: any[];
    predictive: any[];
  }>> => {
    const response = await apiClient.get('/ai/smart-notifications');
    return response.data;
  },

  // Learning path recommendations
  getLearningPathRecommendations: async (params?: {
    skills?: string[];
    goals?: string[];
    timeAvailable?: number;
    difficulty?: 'beginner' | 'intermediate' | 'advanced';
  }): Promise<ApiResponse<{
    recommended: any[];
    reasoning: string[];
    estimatedTime: number;
    skillsToGain: string[];
  }>> => {
    const response = await apiClient.get('/ai/learning-recommendations', { params });
    return response.data;
  },

  // Productivity insights
  getProductivityInsights: async (): Promise<ApiResponse<{
    patterns: any[];
    suggestions: string[];
    optimalTimes: any;
    distractions: any[];
    improvements: any[];
  }>> => {
    const response = await apiClient.get('/ai/productivity-insights');
    return response.data;
  },

  // Team dynamics analysis
  getTeamDynamicsAnalysis: async (teamId?: string): Promise<ApiResponse<{
    collaboration: any;
    communication: any;
    morale: any;
    productivity: any;
    recommendations: string[];
  }>> => {
    const params = teamId ? { teamId } : {};
    const response = await apiClient.get('/ai/team-dynamics', { params });
    return response.data;
  },

  // Predictive analytics
  getPredictiveAnalytics: async (params?: {
    type?: 'burnout' | 'engagement' | 'performance' | 'turnover';
    timeframe?: 'week' | 'month' | 'quarter';
  }): Promise<ApiResponse<{
    predictions: any[];
    confidence: number;
    factors: any[];
    recommendations: string[];
    preventiveActions: string[];
  }>> => {
    const response = await apiClient.get('/ai/predictive-analytics', { params });
    return response.data;
  },

  // Feedback analysis
  analyzeFeedback: async (data: {
    feedback: string;
    context?: string;
    category?: string;
  }): Promise<ApiResponse<{
    sentiment: string;
    themes: string[];
    actionItems: string[];
    priority: 'low' | 'medium' | 'high';
    suggestedResponse?: string;
  }>> => {
    const response = await apiClient.post('/ai/feedback-analysis', data);
    return response.data;
  },

  // ============================================
  // COMPANY KNOWLEDGE AI
  // ============================================

  // Ask anything about the company — auto-loads all policies, departments, and announcements
  askCompanyQuestion: async (data: {
    question: string;
    conversationHistory?: Array<{ role: string; content: string }>;
    conversationId?: string;
  }): Promise<ApiResponse<{
    response: string;
    conversationId: string;
    mesairaId: string;
    botName: string;
    botPicture: string | null;
    citations: Array<{ id: number; title: string }>;
    companyName: string;
    timestamp: string;
  }>> => {
    const response = await apiClient.post('/ai/chat/company', data);
    return response.data;
  },

  // ============================================
  // POLICY Q&A ENDPOINTS
  // ============================================

  // Chat with AI about company policies
  askPolicyQuestion: async (data: {
    question: string;
    policyIds: number[];
    conversationHistory?: Array<{ role: string; content: string }>;
    conversationId?: string;
  }): Promise<ApiResponse<{
    response: string;
    conversationId: string;
    mesairaId: string;
    botName: string;
    citations: Array<{ id: number; title: string }>;
    timestamp: string;
  }>> => {
    const response = await apiClient.post('/ai/chat/policy', data);
    return response.data;
  },

  // Get suggested questions from policy
  getSuggestedQuestionsForPolicy: async (
    policyId: number
  ): Promise<ApiResponse<{
    policyId: number;
    policyTitle: string;
    suggestedQuestions: string[];
    timestamp: string;
  }>> => {
    const response = await apiClient.get(`/ai/policies/${policyId}/suggested-questions`);
    return response.data;
  },

  // ============================================
  // ADMIN AI BOT CONFIG ENDPOINTS
  // ============================================

  // Get bot configuration
  getBotConfig: async (): Promise<ApiResponse<{
    id?: number;
    companyId: number;
    botName: string;
    botPicture?: string;
    systemPrompt?: string;
    policyIds: string | number[];
    enablePoliciesQA: number;
    enableQuestionGeneration: number;
    isActive: number;
    createdAt?: string;
    updatedAt?: string;
  }>> => {
    const response = await apiClient.get('/ai/admin/bot-config');
    return response.data;
  },

  // Update bot configuration
  updateBotConfig: async (data: {
    botName: string;
    botPicture?: string;
    systemPrompt?: string;
    policyIds: number[];
    enablePoliciesQA: boolean;
    enableQuestionGeneration: boolean;
  }): Promise<ApiResponse<any>> => {
    const response = await apiClient.put('/ai/admin/bot-config', data);
    return response.data;
  },

  // Test bot configuration
  testBotConfig: async (data: {
    question: string;
    policyIds: number[];
  }): Promise<ApiResponse<{
    testQuestion: string;
    response: string;
    policiesUsed: number;
    timestamp: string;
  }>> => {
    const response = await apiClient.post('/ai/admin/bot-config/test', data);
    return response.data;
  },};